/**
 * Orkestrator E4 §3 (display part) — "N messages not delivered: <plain reason>" inside the account
 * notifications card. Reads peer B's summary; renders nothing when the endpoint is absent or the count is 0.
 */
import { useUndeliveredNotifications } from '#/lib/api-workspace-settings'
import { m } from '#/paraglide/messages'

const reasonLabel = (r: string) =>
  r === 'no_channel' ? m.sorx_reason_no_channel() : r === 'rate_limited' ? m.sorx_reason_rate_limited() : r === 'disabled' ? m.sorx_reason_disabled() : m.sorx_reason_other()

const reasonHint = (r: string | undefined) =>
  r === 'no_channel' ? m.sorx_hint_no_channel() : r === 'rate_limited' ? m.sorx_hint_rate_limited() : r === 'disabled' ? m.sorx_hint_disabled() : null

/** Reasons with a positive count, biggest first. */
export function rankReasons(byReason: Record<string, number | undefined> | null | undefined): [string, number][] {
  return Object.entries(byReason ?? {})
    .filter((e): e is [string, number] => typeof e[1] === 'number' && e[1] > 0)
    .sort((a, b) => b[1] - a[1])
}

export function UndeliveredNotice({ enabled, fixHref = '#notify-add' }: { enabled: boolean; fixHref?: string }) {
  const { data } = useUndeliveredNotifications(enabled)
  if (!data || !(data.total > 0)) return null
  const [top, ...rest] = rankReasons(data.byReason)
  const reason = top ? reasonLabel(top[0]) : m.sorx_reason_other()
  const hint = reasonHint(top?.[0])
  return (
    <div role="status" data-testid="undelivered-notice" className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-status-progress/40 bg-status-progress/5 p-3 text-sm">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-status-progress">
          {data.total === 1 ? m.sorx_undelivered_one({ reason }) : m.sorx_undelivered_many({ count: data.total, reason })}
        </p>
        {rest.length ? (
          <p className="mt-1 text-xs text-muted-foreground">{m.sorx_undelivered_also({ list: rest.map(([r, n]) => `${reasonLabel(r)} (${n})`).join(', ') })}</p>
        ) : null}
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </div>
      <a href={fixHref} className="shrink-0 font-medium text-primary underline-offset-4 hover:underline">
        {m.sorx_undelivered_fix()}
      </a>
    </div>
  )
}
